
import { ArrowForwardOutlined } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";

import type { WidgetProps } from "./types";

// Card grid of recent posts. v0.5: hand-edited in fixture. v1: provisioning
// pipeline fetches recent posts from bisoncommerce.com (RSS, Webflow CMS API,
// or scrape) and persists into the room JSON before sending the magic link.
//
// Same shape v1 will produce — only the data source changes.
export function BlogPosts({ widget }: WidgetProps<"blog_posts">) {
    const { title, description, posts } = widget.props;

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: description ? 0.5 : 2 }}>
                {title}
            </Typography>
            {description && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, maxWidth: 720 }}
                >
                    {description}
                </Typography>
            )}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                    gap: 2.5,
                }}
            >
                {posts.map((post) => (
                    <PostCard key={post.url} post={post} />
                ))}
            </Box>
        </Box>
    );
}

type Post = WidgetProps<"blog_posts">["widget"]["props"]["posts"][number];

function PostCard({ post }: { post: Post }) {
    return (
        <Stack
            component="a"
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            spacing={0}
            sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: "10px",
                overflow: "hidden",
                bgcolor: "background.paper",
                textDecoration: "none",
                color: "inherit",
                transition: "border-color 120ms, transform 120ms",
                "&:hover": {
                    borderColor: "primary.main",
                    transform: "translateY(-2px)",
                },
            }}
        >
            {post.imageUrl ? (
                <Box
                    component="img"
                    src={post.imageUrl}
                    alt={post.title}
                    sx={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        background: (t) =>
                            `linear-gradient(135deg, ${t.palette.primary.main} 0%, #003459 100%)`,
                    }}
                />
            )}
            <Stack spacing={1.25} sx={{ p: 2.5, flex: 1 }}>
                {post.category && (
                    <Typography variant="overline">
                        {post.category}
                    </Typography>
                )}
                <Typography variant="h6" sx={{ fontSize: "1.0625rem", lineHeight: 1.35 }}>
                    {post.title}
                </Typography>
                {post.excerpt && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {post.excerpt}
                    </Typography>
                )}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: "auto", pt: 0.5 }}
                >
                    <Typography variant="caption" color="text.secondary">
                        {[post.author, post.date].filter(Boolean).join(" · ")}
                    </Typography>
                    <ArrowForwardOutlined sx={{ fontSize: 18, color: "secondary.main" }} />
                </Stack>
            </Stack>
        </Stack>
    );
}
